import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageTitle, Card, EmptyState, LoadingSkeleton, Button } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockCourses, mockEnrollments } from '../mockData';
import { Course } from '../types';

const COURSES_PER_PAGE = 8;
const DEFAULT_COURSE_IMAGE = 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=870&q=80';

// Icons
const ChevronLeftIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const ChevronRightIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const StarIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
    <svg className={`${className} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);


const CourseCard: React.FC<{ 
    course: Course;
}> = ({ course }) => {
    
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.src = DEFAULT_COURSE_IMAGE;
    };

    return (
        <Card className="overflow-hidden flex flex-col group transition-all duration-300">
            <div className="relative">
                <Link to={`/courses/${course.id}`} className="block overflow-hidden h-48 rounded-t-2xl">
                    <img 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        src={course.imageUrl || DEFAULT_COURSE_IMAGE} 
                        alt={course.title}
                        onError={handleImageError}
                    />
                </Link>
            </div>
            <div className="p-4 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-3">
                    <img src={course.instructorAvatar} alt={course.instructor} className="w-8 h-8 rounded-full object-cover" />
                    <span className="text-sm font-semibold text-gray-800 flex-grow">{course.instructor}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{course.category}</span>
                </div>
                <h3 className="text-md font-bold text-gray-900 leading-snug flex-grow mb-2 group-hover:text-primary transition-colors">
                    <Link to={`/courses/${course.id}`}>{course.title}</Link>
                </h3>
                <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-4">
                    <StarIcon />
                    <span className="font-bold text-gray-800">{course.rating}</span>
                    <span>({course.reviewCount} Reviews)</span>
                </div>
                <div className="flex justify-end items-center mt-auto">
                    <Button as={Link} to={`/courses/${course.id}`} size="sm" variant='primary'>
                        View Course
                    </Button>
                </div>
            </div>
        </Card>
    );
};

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="flex flex-wrap justify-between items-center mt-8 gap-4">
            <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </p>
            <nav className="flex items-center space-x-1">
                <button 
                    onClick={() => onPageChange(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Previous page"
                >
                    <ChevronLeftIcon />
                </button>
                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => onPageChange(number)}
                        className={`w-9 h-9 text-sm font-medium rounded-full flex items-center justify-center transition-colors ${currentPage === number ? 'bg-primary text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        aria-current={currentPage === number ? 'page' : undefined}
                    >
                        {number}
                    </button>
                ))}
                 <button 
                    onClick={() => onPageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Next page"
                >
                    <ChevronRightIcon />
                </button>
            </nav>
        </div>
    );
};

const CoursesPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState('enrolled');

    const { enrolledCourses, activeCourses, completedCourses, counts } = useMemo(() => {
        const currentUser = auth?.currentUser;
        if (!currentUser) return { enrolledCourses: [], activeCourses: [], completedCourses: [], counts: { enrolled: 0, active: 0, completed: 0 } };

        const enrolled = mockCourses.filter(c => currentUser.enrolledCourseIds.includes(c.id));
        const userEnrollments = mockEnrollments.filter(e => e.userId === currentUser.id);

        const completed: Course[] = [];
        const active: Course[] = [];

        enrolled.forEach(course => {
            const enrollment = userEnrollments.find(e => e.courseId === course.id);
            const totalLessons = (course.sections || []).flatMap(s => s.lessons).length;

            if (enrollment && totalLessons > 0 && enrollment.completedLessonIds.length === totalLessons) {
                completed.push(course);
            } else {
                active.push(course);
            }
        });

        return {
            enrolledCourses: enrolled,
            activeCourses: active,
            completedCourses: completed,
            counts: {
                enrolled: enrolled.length,
                active: active.length,
                completed: completed.length,
            }
        };
    }, [auth?.currentUser]);

    const filteredCourses = useMemo(() => {
        switch (activeFilter) {
            case 'active':
                return activeCourses;
            case 'completed':
                return completedCourses;
            case 'enrolled':
            default:
                return enrolledCourses;
        }
    }, [activeFilter, enrolledCourses, activeCourses, completedCourses]);
    
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    const paginatedCourses = useMemo(() => {
        const startIndex = (currentPage - 1) * COURSES_PER_PAGE;
        const endIndex = startIndex + COURSES_PER_PAGE;
        return filteredCourses.slice(startIndex, endIndex);
    }, [filteredCourses, currentPage]);

    const totalPages = Math.ceil(filteredCourses.length / COURSES_PER_PAGE);

    const handleFilterChange = (filter: string) => {
        setActiveFilter(filter);
        setCurrentPage(1);
    };

    if (isLoading) {
        return (
             <div>
                <LoadingSkeleton className="h-9 w-1/3 mb-4" />
                <LoadingSkeleton className="h-5 w-1/2 mb-8" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(8)].map((_, i) => <LoadingSkeleton key={i} className="h-96 rounded-xl" />)}
                </div>
            </div>
        );
    }
    
    return (
        <div>
            <PageTitle title="Enrolled Courses" subtitle="Continue your learning journey." />
            
             <div className="flex flex-wrap items-center gap-2 mb-6">
                <button 
                    onClick={() => handleFilterChange('enrolled')} 
                    className={`py-2 px-4 rounded-lg text-sm transition-colors ${activeFilter === 'enrolled' ? 'bg-white border border-primary text-primary shadow-sm font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent font-medium'}`}
                >
                    Enrolled ({counts.enrolled})
                </button>
                <button 
                    onClick={() => handleFilterChange('active')} 
                     className={`py-2 px-4 rounded-lg text-sm transition-colors ${activeFilter === 'active' ? 'bg-white border border-primary text-primary shadow-sm font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent font-medium'}`}
                >
                    Active ({counts.active})
                </button>
                <button 
                    onClick={() => handleFilterChange('completed')} 
                    className={`py-2 px-4 rounded-lg text-sm transition-colors ${activeFilter === 'completed' ? 'bg-white border border-primary text-primary shadow-sm font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent font-medium'}`}
                >
                    Completed ({counts.completed})
                </button>
            </div>


            {paginatedCourses.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedCourses.map(course => <CourseCard 
                            key={course.id} 
                            course={course}
                        />)}
                    </div>
                    {totalPages > 1 && (
                       <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    )}
                </>
            ) : (
                <EmptyState 
                    title="No courses found"
                    message="You do not have any courses in this category."
                />
            )}
        </div>
    );
};

export default CoursesPage;
